import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Observable, of as observableOf, throwError as observableThrowError, zip } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { FeatureFlags } from '@app/core/feature-flags/feature-flags';
import { LaunchDarklyService } from '@app/core/feature-flags/launchdarkly.service';
import {
  LOGIN_EMAIL_EXISTS_ERROR,
  LOGIN_EMAIL_FORM_FIELD,
  MODULE_ACCOUNT_CREATION_PAGE,
  WORK_LOGIN_EMAIL_ERROR,
} from '@app/core/mixpanel.constants';
import { UserService } from '@app/core/user.service';
import { StepName } from '@app/registration/enterprise/registration-step-name';
import { Address } from '@app/shared/address';
import { SendAppLinkGraphQLService } from '@app/shared/download-app-card/send-app-link-graphql.service';
import { User } from '@app/shared/user';

import { CreateAccountStepComponent } from '../../common/create-account/create-account-step.component';
import { ValidateEmailService } from '../../validate-email.service';
import { ValidatePasswordService } from '../../validate-password.service';
import { EnterpriseRegistration } from '../enterprise-registration';
import { EnterpriseRegistrationAnalyticsService } from '../enterprise-registration-analytics.service';
import { IRegistrationStep, RegistrationStep } from '../registration-step';

@Injectable()
export class CreateAccountConfig extends RegistrationStep implements IRegistrationStep {
  GA_LABEL = 'Create_Your_Account_Step';

  MODULE = MODULE_ACCOUNT_CREATION_PAGE;
  captchaNeeded = true;
  component = CreateAccountStepComponent;
  componentInstance: CreateAccountStepComponent;
  progress = 60;
  address: Address;

  form = this.formBuilder.group({
    logInEmail: ['', Validators.required],
    password: ['', Validators.required],
  });

  constructor(
    private enterpriseRegistrationAnalyticsService: EnterpriseRegistrationAnalyticsService,
    private emailService: ValidateEmailService,
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private passwordService: ValidatePasswordService,
    private sendAppLinkGraphQLService: SendAppLinkGraphQLService,
    private launchDarklyService: LaunchDarklyService,
  ) {
    super();
  }

  canGoBack() {
    return true;
  }

  initComponent(component: CreateAccountStepComponent, state: EnterpriseRegistration) {
    component.form = this.form;
    const emailControl = this.form.get('logInEmail');

    this.launchDarklyService
      .featureFlag$(FeatureFlags.ENTERPRISE_REGISTRATION_DISALLOW_WORK_EMAIL_FOR_LOGIN, false)
      .subscribe(flagEnabled => {
        if (!flagEnabled) {
          return;
        }

        if (!emailControl) {
          return;
        }

        // We are only using b2b domains from b2bCompany.b2bEmailDomains, Work email domain is not used
        // because some compaines do not issue work email, in these cases personal e-mail is used.
        const companyDomains: string[] = [];
        companyDomains.push(...(state?.b2bCompany?.b2bEmailDomains ?? []));

        if (companyDomains.length === 0) {
          return;
        }

        emailControl.setValidators([Validators.required, this.validateEmailDomain(companyDomains)]);
        emailControl.updateValueAndValidity();
      });

    // Subscribe to value changes to update error message
    emailControl?.valueChanges.subscribe(() => {
      if (emailControl.hasError('domainMatch')) {
        this.componentInstance.highlightWorkEmailWarning = true;
        this.enterpriseRegistrationAnalyticsService.regInputErrored({
          error: WORK_LOGIN_EMAIL_ERROR,
          formField: LOGIN_EMAIL_FORM_FIELD,
          isWhitelist: state.isWhitelisted,
          companyId: state.b2bCompanyId,
          module: this.MODULE,
        });
      } else if (!emailControl.hasError('domainMatch')) {
        if (this.componentInstance) {
          this.componentInstance.highlightWorkEmailWarning = false;
        }
      }
    });
    emailControl?.updateValueAndValidity();

    component.trackResetPasswordClicked = () =>
      this.enterpriseRegistrationAnalyticsService.resetPasswordClicked({
        module: this.MODULE,
        isWhitelist: state.isWhitelisted,
        companyId: state.b2bCompanyId,
        serviceArea: state.serviceArea.name,
      });

    component.trackEmailAdminClicked = () =>
      this.enterpriseRegistrationAnalyticsService.emailAdminClicked({
        module: this.MODULE,
        isWhitelist: state.isWhitelisted,
        companyId: state.b2bCompanyId,
        serviceArea: state.serviceArea.name,
      });

    this.componentInstance = component;
  }

  onDestroy() {
    this.form.controls.logInEmail.clearValidators();
    this.form.controls.password.clearValidators();
  }

  submit(state: EnterpriseRegistration, captcha: any): Observable<any> {
    if (this.form.invalid) {
      return observableThrowError(new Error());
    }
    const { logInEmail, password } = this.form.value;
    state.patient.email = logInEmail;
    state.patient.password = password;
    return this.createNewUser(captcha, logInEmail, state, password);
  }

  private createNewUser(captcha: any, logInEmail: any, state: EnterpriseRegistration, password: any) {
    const uniqueness = captcha.getToken().pipe(
      switchMap((reCaptchaToken: string) =>
        this.emailService.validateUniqueness(logInEmail, reCaptchaToken).pipe(
          map(() => true),
          catchError((err: string) => {
            this.componentInstance.emailError = err;
            this.trackUniquenessError(state);
            return observableOf(false);
          }),
        ),
      ),
    );
    const complexity = this.passwordService.validateComplexity(logInEmail, password).pipe(
      map(() => true),
      catchError((err: string) => {
        this.componentInstance.passwordError = err;
        return observableOf(false);
      }),
    );

    return zip(uniqueness, complexity).pipe(
      switchMap(([unique, complex]) => {
        if (unique && complex) {
          this.trackSubmission(state);
          return captcha.getToken().pipe(
            switchMap((reCaptchaToken: string) =>
              this.userService.createUser(state.patient, { recaptchaToken: reCaptchaToken }),
            ),
            catchError(err => {
              if (typeof err === 'string') {
                this.componentInstance.passwordError = err;
              } else {
                this.componentInstance.passwordError =
                  'Something went wrong while creating your account. Already have an account? ' +
                  '<a href="/login">Activate your sponsored membership</a>. ' +
                  'For immediate help, contact <a href="mailto:techsupport@onemedical.com">techsupport@onemedical.com</a>.';
              }
              return observableThrowError(err);
            }),
          );
        } else {
          throw new Error();
        }
      }),
      tap((user: User) => {
        this.finishSubmit(state, user);
      }),
    );
  }

  private finishSubmit(state: EnterpriseRegistration, user: User) {
    // save id for later use in registration steps
    state.patient.id = user.id;
    const { phoneNumber, textAppToPhone } = state.form.value.accountSetUp;
    if (textAppToPhone) {
      this.sendAppLinkGraphQLService
        .mutate({
          input: {
            phoneNumber: phoneNumber,
          },
        })
        .subscribe();
    }
    this.enterpriseRegistrationAnalyticsService.identifyAndUpdateUser(user);
    state.setCurrentStep(StepName.termsOfService);
  }

  private trackSubmission(state: EnterpriseRegistration) {
    this.enterpriseRegistrationAnalyticsService.regInputSubmitted({
      isWhitelist: state.isWhitelisted,
      module: this.MODULE,
      relationshipType: state.details.membershipType,
    });
    this.enterpriseRegistrationAnalyticsService.trackWithLaunchDarkly({
      key: 'Enterprise Registration Completion',
    });
  }

  private trackUniquenessError(state: EnterpriseRegistration) {
    this.enterpriseRegistrationAnalyticsService.regInputErrored({
      error: LOGIN_EMAIL_EXISTS_ERROR,
      formField: LOGIN_EMAIL_FORM_FIELD,
      isWhitelist: state.isWhitelisted,
      companyId: state.b2bCompanyId,
      module: this.MODULE,
    });
  }

  /**
   * Custom form validator to check if email domain matches company domain
   */
  private validateEmailDomain(companyDomains: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const email = control.value;

      // If no email or no @ symbol, let other validators handle it
      if (!email || email.indexOf('@') === -1) {
        return null;
      }

      // Extract domain from email
      const emailParts = email.split('@');
      const emailDomain = emailParts[1].toLowerCase();

      // Check if email domain matches any of the company domains
      for (const domain of companyDomains) {
        if (emailDomain === domain.toLowerCase()) {
          return { domainMatch: true };
        }
      }

      return null;
    };
  }
}
